import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import { Badge, DataTable, EmptyState, formatBytes, formatRelative, Input, PageHeader, Pagination, Panel } from "@hostdock/ui";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";
import { useListParams } from "../lib/list-params";

export function UsagePage() {
  return <RequirePermission anyOf={["CUSTOMER_VIEW"]}><Usage /></RequirePermission>;
}

const PAGE_SIZE = 50;

function Usage() {
  const { values, page, set } = useListParams(["search"] as const);
  const q = useQuery({ queryKey: ["admin-usage", values, page], queryFn: () => api.usage.customers({ ...values, page, pageSize: PAGE_SIZE }), placeholderData: keepPreviousData });
  return (
    <>
      <PageHeader title="Customer usage" description="Resource usage per customer, from measured data only. Storage shows “Unavailable” until a customer's websites have reported usage — it is never estimated." />
      <Panel flush>
        <div className="border-b border-line p-3">
          <Input value={values.search} onChange={(e) => set({ search: e.target.value })} placeholder="Search customers…" className="max-w-xs" aria-label="Search customers" />
        </div>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(c) => c.customerId}
          empty={<EmptyState icon={BarChart3} title="No customers" />}
          columns={[
            { key: "n", header: "Customer", cell: (c) => <div><p className="font-medium">{c.name}</p>{c.status !== "ACTIVE" && <Badge tone="warning">{c.status.toLowerCase()}</Badge>}</div> },
            { key: "s", header: "Plans", cell: (c) => c.subscriptions },
            { key: "w", header: "Websites", cell: (c) => c.websites },
            { key: "d", header: "Databases", cell: (c) => c.databases },
            { key: "z", header: "Storage", cell: (c) => (c.storageBytes === null ? <span className="text-muted">Unavailable</span> : formatBytes(c.storageBytes)) },
            { key: "m", header: "Last measured", cell: (c) => <span className="text-muted">{c.lastMeasuredAt ? formatRelative(c.lastMeasuredAt) : "Never"}</span>, hideOnMobile: true },
          ]}
        />
        <Pagination page={page} pageSize={PAGE_SIZE} total={q.data?.total ?? 0} onPage={(p) => set({ page: String(p) })} />
      </Panel>
    </>
  );
}
